import React, { useState } from "react";
import { Building2, Mail, Lock, Eye, EyeOff, LogIn, UserPlus, Info } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const OrganizerAuth = ({ isOpen, onClose, onAuthenticated }) => {
  const [activeTab, setActiveTab] = useState("login");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetData, setResetData] = useState({ token: "", new_password: "", confirm_password: "" });
  
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });
  
  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    company_name: "",
    description: "",
  });

  // Check URL for reset token on mount
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resetToken = params.get("reset_token");
    if (resetToken) {
      setResetData({ ...resetData, token: resetToken });
      setShowReset(true);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleLoginChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };

  const handleRegisterChange = (e) => {
    setRegisterData({ ...registerData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/organizer/login`, loginData);
      
      if (response.data.access_token) {
        localStorage.setItem("tika_organizer_token", response.data.access_token);
        localStorage.setItem("tika_organizer", JSON.stringify(response.data.organizer));
        toast.success(`Bienvenue ${response.data.organizer.company_name || response.data.organizer.name} !`);
        onAuthenticated(response.data.organizer);
      }
    } catch (error) {
      console.error("Erreur de connexion:", error);
      toast.error(error.response?.data?.detail || "Email ou mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.name || !registerData.email || !registerData.password) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    if (registerData.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/organizer/register`, {
        name: registerData.name,
        email: registerData.email,
        password: registerData.password,
        company_name: registerData.company_name,
        description: registerData.description,
      });
      
      if (response.data.access_token) {
        localStorage.setItem("tika_organizer_token", response.data.access_token);
        localStorage.setItem("tika_organizer", JSON.stringify(response.data.organizer));
        toast.success(`Bienvenue sur TIKA, ${response.data.organizer.company_name || response.data.organizer.name} !`);
        onAuthenticated(response.data.organizer);
      }
    } catch (error) {
      console.error("Erreur d'inscription:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error("Entrez votre email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/organizer/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success("Email de réinitialisation envoyé !");
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetData.new_password !== resetData.confirm_password) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (resetData.new_password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/organizer/reset-password`, {
        token: resetData.token,
        new_password: resetData.new_password
      });
      toast.success("Mot de passe réinitialisé ! Connectez-vous.");
      setShowReset(false);
      setActiveTab("login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="organizer-auth-description">
        <DialogHeader>
          <DialogTitle className="text-2xl text-center font-bold flex items-center justify-center gap-2">
            <Building2 className="w-6 h-6 text-primary" />
            Espace Organisateur
          </DialogTitle>
          <p id="organizer-auth-description" className="sr-only">Connexion ou inscription pour les organisateurs d'événements</p>
        </DialogHeader>

        {/* Forgot Password View */}
        {showForgot && !showReset && (
          <div className="mt-4 space-y-4">
            {forgotSent ? (
              <div className="text-center py-4 space-y-3">
                <Mail className="w-12 h-12 mx-auto text-primary" />
                <h3 className="font-bold">Email envoyé !</h3>
                <p className="text-sm text-muted-foreground">
                  Si un compte existe avec l'adresse <strong>{forgotEmail}</strong>, vous recevrez un lien de réinitialisation. Pensez à vérifier vos spams.
                </p>
                <Button variant="outline" onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(""); }}>
                  Retour à la connexion
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <h3 className="font-bold text-center">Mot de passe oublié</h3>
                <p className="text-sm text-muted-foreground text-center">
                  Entrez votre email, nous vous enverrons un lien de réinitialisation.
                </p>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="votre@email.com"
                    className="pl-10"
                    required
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? "Envoi..." : "Envoyer le lien"}
                </Button>
                <button type="button" onClick={() => setShowForgot(false)} className="w-full text-center text-xs text-muted-foreground hover:text-primary">
                  Retour à la connexion
                </button>
              </form>
            )}
          </div>
        )}

        {/* Reset Password View */}
        {showReset && (
          <div className="mt-4">
            <form onSubmit={handleResetPassword} className="space-y-4">
              <h3 className="font-bold text-center">Nouveau mot de passe</h3>
              <div>
                <Label>Nouveau mot de passe</Label>
                <Input
                  type="password"
                  value={resetData.new_password}
                  onChange={(e) => setResetData({ ...resetData, new_password: e.target.value })}
                  placeholder="Min. 6 caractères"
                  className="mt-2"
                  required
                />
              </div>
              <div>
                <Label>Confirmer le mot de passe</Label>
                <Input
                  type="password"
                  value={resetData.confirm_password}
                  onChange={(e) => setResetData({ ...resetData, confirm_password: e.target.value })}
                  placeholder="Retapez le mot de passe"
                  className="mt-2"
                  required
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? "Réinitialisation..." : "Réinitialiser le mot de passe"}
              </Button>
            </form>
          </div>
        )}

        {/* Normal Login/Register Tabs */}
        {!showForgot && !showReset && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" className="flex items-center gap-2">
              <LogIn className="w-4 h-4" />
              Connexion
            </TabsTrigger>
            <TabsTrigger value="register" className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Inscription
            </TabsTrigger>
          </TabsList>

          {/* Login Form */}
          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="org-login-email">Email</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="org-login-email"
                    id="org-login-email"
                    name="email"
                    type="email"
                    value={loginData.email}
                    onChange={handleLoginChange}
                    placeholder="organisateur@email.com"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="org-login-password">Mot de passe</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="org-login-password"
                    id="org-login-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={loginData.password}
                    onChange={handleLoginChange}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                data-testid="org-login-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90"
              >
                {loading ? "Connexion..." : "Se connecter"}
              </Button>

              <button
                type="button"
                onClick={() => setShowForgot(true)}
                className="w-full text-center text-xs text-muted-foreground hover:text-primary transition-colors mt-2"
              >
                Mot de passe oublié ?
              </button>
            </form>
          </TabsContent>

          {/* Register Form */}
          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-4 mt-4">
              <div className="bg-primary/10 rounded-lg p-3 flex gap-2">
                <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  Créez votre espace organisateur pour publier vos événements et gérer votre communauté.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="org-name">Votre nom *</Label>
                  <Input
                    data-testid="org-register-name"
                    id="org-name"
                    name="name"
                    value={registerData.name}
                    onChange={handleRegisterChange}
                    placeholder="Jean Dupont"
                    className="mt-2"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="org-company">Nom de l'organisation</Label>
                  <Input
                    data-testid="org-register-company"
                    id="org-company"
                    name="company_name"
                    value={registerData.company_name}
                    onChange={handleRegisterChange}
                    placeholder="TIKA Events"
                    className="mt-2"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="org-email">Email *</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="org-register-email"
                    id="org-email"
                    name="email"
                    type="email"
                    value={registerData.email}
                    onChange={handleRegisterChange}
                    placeholder="contact@organisation.com"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="org-description">Description courte</Label>
                <Textarea
                  data-testid="org-register-description"
                  id="org-description"
                  name="description"
                  value={registerData.description}
                  onChange={handleRegisterChange}
                  placeholder="Présentez votre organisation en quelques mots..."
                  rows={3}
                  className="mt-2"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="org-password">Mot de passe *</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="org-register-password"
                      id="org-password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={registerData.password}
                      onChange={handleRegisterChange}
                      placeholder="Min. 6 caractères"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="org-confirm">Confirmer *</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="org-register-confirm"
                      id="org-confirm"
                      name="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      value={registerData.confirmPassword}
                      onChange={handleRegisterChange}
                      placeholder="••••••••"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              </div>

              <Button
                data-testid="org-register-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90"
              >
                {loading ? "Création..." : "Créer mon espace organisateur"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
